// Shop pricing — ONE implementation shared by the React cart, the build-time
// static pages and (mirrored line-for-line) backend/shop_pricing.py. The backend
// recomputes every total from the published catalogue and never trusts the
// browser; if you change a rule here, change it there and re-run the parity
// test (backend/tests/test_shop.py reads fixtures generated from this file).
//
// All arithmetic is in integer CENTS. Rounding is half-up on non-negative values.
const { installTiers, installAddons, shipping, installAreas, findVariant, supplierCodeFor } = require('../content/products');
const { serviceAreas } = require('../content/serviceAreas');

const GST_RATE = 0.15;

function roundHalfUp(x) {
  return Math.floor(x + 0.5);
}
function toCents(dollars) {
  return roundHalfUp(dollars * 100);
}
function exFromInc(cents) {
  return roundHalfUp(cents / (1 + GST_RATE));
}
function incFromEx(cents) {
  return roundHalfUp(cents * (1 + GST_RATE));
}
function gstPortion(incCents) {
  return incCents - exFromInc(incCents);
}
function fmt(cents) {
  const dollars = cents / 100;
  return '$' + dollars.toLocaleString('en-NZ', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
function fmtWhole(cents) {
  return '$' + Math.round(cents / 100).toLocaleString('en-NZ');
}

function tierIncCents(tierKey) {
  const t = installTiers[tierKey];
  if (!t) return 0;
  return incFromEx(toCents(t.exGst));
}
function travelIncCents() {
  return incFromEx(toCents(installAreas.travelSurchargeExGst));
}

// Suburb classification for the install area (plan D5).
// Returns 'standard' | 'outer' | 'unknown'.
function suburbZone(name) {
  if (!name) return 'unknown';
  const n = String(name).trim().toLowerCase();
  if (serviceAreas.some((a) => a.name.toLowerCase() === n || a.slug === n)) return 'standard';
  if (installAreas.central.some((s) => s.toLowerCase() === n)) return 'standard';
  if (installAreas.outer.some((s) => s.toLowerCase() === n)) return 'outer';
  return 'unknown';
}

// The 3-question door check → install tier (plan §6.3). The product's own
// default tier is the floor for cut-in doors; a like-for-like swap can drop
// to Standard; unusual doors go Bespoke.
//   doorMaterial: 'timber' | 'aluminium' | 'other'
//   existingLock: 'same-type' | 'different' | 'none' | 'unsure'
//   sliding: boolean
function resolveTier(product, answers) {
  const a = answers || {};
  const productTier = (product && product.install && product.install.tier) || 'standard';
  if (a.doorMaterial === 'other') return 'bespoke';
  if (productTier === 'bespoke') return 'bespoke';
  if (a.sliding || (product && product.fit && product.fit.sliding)) return 'cut';
  if (a.existingLock === 'unsure') return 'cut';
  if (a.existingLock === 'same-type') return productTier === 'swap' ? 'swap' : 'standard';
  if (a.doorMaterial === 'aluminium') return 'cut';
  if (a.existingLock === 'none' || a.existingLock === 'different') return 'cut';
  return productTier;
}

// cart = { items: [{ variantId, qty, backsetMm }], install: { selected, doorMaterial,
// existingLock, sliding, suburb } | null, shippingChoice: 'standard' | 'rural' }
// Returns a fully itemised quote in cents, or { error } for an invalid cart.
function quoteCart(cart) {
  const items = [];
  let itemsCents = 0;
  let installCount = 0;
  let firstEligible = null;
  for (const line of cart.items || []) {
    const hit = findVariant(line.variantId);
    if (!hit) return { error: `Unknown item ${line.variantId}` };
    const qty = Math.max(1, Math.min(10, parseInt(line.qty, 10) || 1));
    const noMortice = !!(line.noMortice && hit.product.mortice);
    const unit = toCents(hit.product.priceIncGst) + (noMortice ? toCents(hit.product.mortice.leversetOnlyDeltaIncGst) : 0);
    const saleStatus = hit.product.saleStatus || 'sale';
    if (saleStatus === 'quote') return { error: `${hit.product.shortName} is quoted per door — request a quote instead of buying online` };
    if (saleStatus === 'coming-soon') return { error: `${hit.product.shortName} is coming soon and can't be ordered yet` };
    // The backset is not asked for before payment (Hugh, 17 Sep 2026): a lock that comes in
    // sizes goes through with backsetMm null and a '?' in the supplier code, and the size is
    // read off the door photos afterwards (backsetPending flags the order as needing them).
    // A size the line does carry must be one the lock comes in.
    const backsets = hit.product.fit.backsetsMm || [];
    const sized = !noMortice && backsets.length > 0;
    const backsetMm = sized && line.backsetMm != null && backsets.includes(Number(line.backsetMm)) ? Number(line.backsetMm) : null;
    if (sized && line.backsetMm != null && backsetMm === null) {
      return { error: `${hit.product.shortName} does not come in ${line.backsetMm} mm` };
    }
    items.push({
      variantId: hit.variant.id,
      productSlug: hit.product.slug,
      name: hit.product.name,
      shortName: hit.product.shortName,
      variantLabel: hit.variant.label,
      backsetMm,
      backsetPending: sized && backsetMm === null,
      noMortice,
      backsetMeasuredMm: line.backsetMeasuredMm ? Number(line.backsetMeasuredMm) : null,
      supplier: hit.variant.supplier,
      supplierSku: supplierCodeFor(hit.product, hit.variant, backsetMm, noMortice),
      qty,
      unitCents: unit,
      lineCents: unit * qty,
      installEligible: !!(hit.product.install && hit.product.install.eligible),
      saleStatus,
    });
    itemsCents += unit * qty;
    if (hit.product.install && hit.product.install.eligible) {
      installCount += qty;
      if (!firstEligible) firstEligible = hit.product;
    }
  }
  if (items.length === 0) return { error: 'Your cart is empty' };

  const install = cart.install && cart.install.selected && installCount > 0 ? cart.install : null;
  const installOnly = items.find((i) => i.saleStatus === 'install-only');
  if (installOnly && !install) {
    return { error: `${installOnly.shortName} is supply & install only (Auckland) — add installation or remove it` };
  }
  let installTier = null;
  let installUnitCents = 0;
  let installCents = 0;
  let travelCents = 0;
  let hubSetupCents = 0;
  let zone = null;
  if (install) {
    installTier = resolveTier(firstEligible, install);
    installUnitCents = tierIncCents(installTier);
    installCents = installUnitCents * installCount;
    zone = suburbZone(install.suburb);
    if (zone !== 'standard') travelCents = travelIncCents();
    if (install.hubSetup) hubSetupCents = incFromEx(toCents(installAddons.hubSetup.exGst));
  }

  let shippingCents = 0;
  let shippingLabel = 'No shipping — fitted by Shore Doors';
  if (!install) {
    const threshold = shipping.freeOver ? toCents(shipping.freeOver) : null;
    if (threshold !== null && itemsCents >= threshold) {
      shippingCents = 0;
      shippingLabel = `Free NZ-wide courier (orders over ${fmtWhole(threshold)})`;
    } else if (cart.shippingChoice === 'rural') {
      shippingCents = toCents(shipping.rural);
      shippingLabel = 'Rural courier';
    } else {
      shippingCents = toCents(shipping.standard);
      shippingLabel = 'Courier';
    }
  }

  const totalCents = itemsCents + installCents + travelCents + hubSetupCents + shippingCents;
  return {
    items,
    itemsCents,
    install: install
      ? { tier: installTier, tierLabel: installTiers[installTier].label, count: installCount, unitCents: installUnitCents, cents: installCents, travelCents, hubSetupCents, zone, suburb: install.suburb }
      : null,
    shippingCents,
    shippingLabel,
    shippingChoice: install ? null : cart.shippingChoice === 'rural' ? 'rural' : 'standard',
    totalCents,
    gstCents: gstPortion(totalCents),
    exGstCents: exFromInc(totalCents),
  };
}

// Fitted price shown first on product pages: product + its default tier, one unit.
function fittedFromCents(product) {
  if (!product.install || !product.install.eligible) return null;
  return toCents(product.priceIncGst) + tierIncCents(product.install.tier || 'standard');
}

module.exports = {
  GST_RATE,
  roundHalfUp,
  toCents,
  exFromInc,
  incFromEx,
  gstPortion,
  fmt,
  fmtWhole,
  tierIncCents,
  travelIncCents,
  suburbZone,
  resolveTier,
  quoteCart,
  fittedFromCents,
};
